import { useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Mail, Check, X } from "lucide-react";
import { toast } from "sonner";

// Subtle CTA card that lets users opt in to a monthly bill-summary email
// delivered on the 1st of every month. Actual send is wired via Resend
// (backlog); this stores the opt-in + preferred email so the mailing list
// is ready to go the moment the provider is connected.
export default function MonthlySummaryOptIn({ me, onUpdated }) {
  const alreadyOptedIn = !!me?.monthly_summary_opt_in;
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState(me?.monthly_summary_email || me?.email || "");
  const [saving, setSaving] = useState(false);
  const [dismissed, setDismissed] = useState(() => {
    return typeof window !== "undefined" && localStorage.getItem("monthlySummaryDismissed") === "1";
  });

  const subscribe = async () => {
    setSaving(true);
    try {
      const { data } = await api.post("/settings/monthly-summary", { opt_in: true, email });
      onUpdated && onUpdated(data);
      toast.success("You’re on the list — we’ll email your monthly summary on the 1st.");
      setOpen(false);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const unsubscribe = async () => {
    setSaving(true);
    try {
      const { data } = await api.post("/settings/monthly-summary", { opt_in: false });
      onUpdated && onUpdated(data);
      toast("You’ve unsubscribed from the monthly summary.");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  if (alreadyOptedIn) {
    return (
      <div
        className="flex items-center justify-between gap-3 rounded-2xl border border-[#CFE0CC] bg-[#EEF6EC] px-4 py-2.5 text-sm"
        data-testid="monthly-summary-subscribed"
      >
        <div className="flex items-center gap-2 text-[#3B5B3E] min-w-0">
          <Check size={16} className="shrink-0" />
          <span className="truncate">
            Monthly bill summary will be emailed to{" "}
            <span className="font-semibold">{me.monthly_summary_email}</span> on the 1st.
          </span>
        </div>
        <button
          className="text-xs text-[#59605C] hover:text-[#2C302E] underline"
          onClick={unsubscribe}
          disabled={saving}
          data-testid="monthly-summary-unsubscribe"
        >
          Unsubscribe
        </button>
      </div>
    );
  }

  if (dismissed) return null;

  return (
    <>
      <div
        className="flex items-center justify-between gap-3 rounded-2xl border border-[#E5D9C2] bg-[#FBF4E4] px-4 py-2.5 text-sm"
        data-testid="monthly-summary-cta"
      >
        <div className="flex items-center gap-2 text-[#5A4A20] min-w-0">
          <Mail size={16} className="shrink-0" />
          <span className="truncate">
            Get a monthly bill summary emailed on the 1st — a quick heads-up before the new month kicks off.
          </span>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <button
            className="btn-primary text-xs !px-3 !py-1.5"
            onClick={() => setOpen(true)}
            data-testid="monthly-summary-subscribe-btn"
          >
            Sign me up
          </button>
          <button
            className="text-[#8A918D] hover:text-[#2C302E]"
            aria-label="Dismiss"
            onClick={() => {
              localStorage.setItem("monthlySummaryDismissed", "1");
              setDismissed(true);
            }}
            data-testid="monthly-summary-dismiss"
          >
            <X size={16} />
          </button>
        </div>
      </div>

      {open && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
          onClick={() => setOpen(false)}
          data-testid="monthly-summary-modal-backdrop"
        >
          <div
            className="w-full max-w-md rounded-2xl bg-white shadow-2xl p-6"
            onClick={(e) => e.stopPropagation()}
            data-testid="monthly-summary-modal"
          >
            <div className="flex items-start justify-between mb-3">
              <div>
                <div className="flex items-center gap-2 text-[#4A6148]">
                  <Mail size={18} />
                  <div className="text-xs font-semibold uppercase tracking-widest">Monthly bill summary</div>
                </div>
                <h2 className="font-heading text-xl text-[#2C302E] mt-1">Get a heads-up on the 1st</h2>
                <p className="text-xs text-[#59605C] mt-1 leading-relaxed">
                  On the first of every month you’ll get a compact email summarizing next month’s bills, expected income, and estimated end-of-month cash. Unsubscribe anytime.
                </p>
              </div>
              <button
                className="text-[#8A918D] hover:text-[#2C302E] shrink-0"
                onClick={() => setOpen(false)}
                aria-label="Close"
              >
                <X size={20} />
              </button>
            </div>

            <label className="block mt-2">
              <span className="text-xs font-semibold uppercase tracking-widest text-[#59605C]">Deliver to</span>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-1 w-full px-3 py-2 rounded-xl border border-[#CDC7BB] bg-white text-[#2C302E] focus:outline-none focus:border-[#4A6148]"
                data-testid="monthly-summary-email-input"
              />
            </label>

            <div className="mt-5 flex justify-end gap-2">
              <button className="btn-ghost !px-3 !py-2 text-sm" onClick={() => setOpen(false)} disabled={saving}>Cancel</button>
              <button
                className="btn-primary text-sm"
                onClick={subscribe}
                disabled={saving || !email}
                data-testid="monthly-summary-confirm"
              >
                {saving ? "Signing you up…" : "Subscribe"}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
